import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiClientInterface } from '../../client/api/api-client.interface';
import {
    ContractDataInterface,
    ContractInterface,
    ContractLiteInterface,
    CreatedContractInterface,
    IdInterface,
    OrderClientInterface,
    ProductInterface,
} from '../data';
import { Transaction } from '../transaction/transaction';
import { TransactionRequestInterface } from '../transaction/transaction-request.interface';

@Injectable()
export class ContractApiRepository {
    constructor(private apiClient: ApiClientInterface, private transactionRequest: TransactionRequestInterface) {}

    loadAll(clientCode: IdInterface): Observable<Transaction<ContractLiteInterface[]>> {
        return this.transactionRequest.wrap(this.apiClient.getContracts(clientCode));
    }

    load(): Observable<Transaction<ContractInterface>> {
        return this.transactionRequest.wrap(this.apiClient.getContract());
    }

    loadData(contractCode: IdInterface): Observable<Transaction<ContractDataInterface>> {
        return this.transactionRequest.wrap(this.apiClient.getContractData(contractCode));
    }

    loadSignatories(contractCode: IdInterface, clientCode?: IdInterface): Observable<Transaction<OrderClientInterface[]>> {
        return this.transactionRequest.wrap(this.apiClient.getContractSignatures(contractCode, clientCode));
    }

    cancel(contract: ContractInterface): Observable<Transaction<void>> {
        return this.transactionRequest.wrap(this.apiClient.cancelContract(contract.encryptedCode)).pipe(
            map((transaction) =>
                transaction.map(
                    () => Transaction.success(),
                    (throwable) => Transaction.error(throwable.message)
                )
            )
        );
    }

    create(contract: ContractInterface): Observable<Transaction<void>> {
        return this.transactionRequest.wrap(this.apiClient.createContract(contract)).pipe(
            map((transaction) =>
                transaction.map(
                    () => Transaction.success(),
                    (throwable) => Transaction.error(throwable.message)
                )
            )
        );
    }

    createWithProducts(
        contract: ContractInterface,
        products: ProductInterface[],
        captcha: string
    ): Observable<Transaction<CreatedContractInterface>> {
        return this.transactionRequest.wrap(this.apiClient.createContractWithProducts(contract, products, captcha));
    }

    finalize(contract: ContractInterface): Observable<Transaction<void>> {
        return this.transactionRequest.wrap(this.apiClient.finalizeContract(contract.encryptedCode)).pipe(
            map((transaction) =>
                transaction.map(
                    () => Transaction.success(),
                    (throwable) => Transaction.error(throwable.message)
                )
            )
        );
    }

    reject(contract: ContractInterface, message: string): Observable<Transaction<void>> {
        return this.transactionRequest.wrap(this.apiClient.rejectContract(contract.encryptedCode, message)).pipe(
            map((transaction) =>
                transaction.map(
                    () => Transaction.success(),
                    (throwable) => Transaction.error(throwable.message)
                )
            )
        );
    }

    revise(contract: ContractInterface, message: string): Observable<Transaction<void>> {
        return this.transactionRequest.wrap(this.apiClient.reviseContract(contract.encryptedCode, message)).pipe(
            map((transaction) =>
                transaction.map(
                    () => Transaction.success(),
                    (throwable) => Transaction.error(throwable.message)
                )
            )
        );
    }

    update(contract: ContractInterface): Observable<Transaction<void>> {
        return this.transactionRequest.wrap(this.apiClient.updateContract(contract)).pipe(
            map((transaction) =>
                transaction.map(
                    () => Transaction.success(),
                    (throwable) => Transaction.error(throwable.message)
                )
            )
        );
    }

    validate(contract: ContractInterface): Observable<Transaction<void>> {
        return this.transactionRequest.wrap(this.apiClient.validateContract(contract.encryptedCode)).pipe(
            map((transaction) =>
                transaction.map(
                    () => Transaction.success(),
                    (throwable) => Transaction.error(throwable.message)
                )
            )
        );
    }

    validateNotification(contract: ContractInterface): Observable<Transaction<void>> {
        return this.transactionRequest.wrap(this.apiClient.validateNotification(contract.encryptedCode)).pipe(
            map((transaction) =>
                transaction.map(
                    () => Transaction.success(),
                    (throwable) => Transaction.error(throwable.message)
                )
            )
        );
    }
}
